import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { ExternalUsersExternalsPageItemFragment } from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import UserAvatar from 'modules/core/components/UserAvatar'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import BecomeModal from 'modules/authentication/components/BecomeModal'
import DeactivateExternalAccessModal from 'modules/external_users/components/DeactivateExternalAccessModal'
import EditUserModal from 'modules/external_users/components/EditUserModal'
import UserStatus from 'modules/external_users/components/UserStatus'
import { useInviteAccess } from 'modules/external_users/hooks/useInviteAccess'

interface Props {
  user: ExternalUsersExternalsPageItemFragment
}

const Item = ({ user }: Props) => {
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false)
  const [becomeModalOpen, setBecomeModalOpen] = useState(false)

  const canInvite = user._policies?.invite.can ?? false
  const canBecome = user._policies?.become.can ?? false
  const canUpdate = user._policies?.update.can ?? false

  const { invite } = useInviteAccess({
    onCompleted(data) {
      if (data.apiCoreMutations.inviteApicoreAccess?.errors.length) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('settings.external_users.actions.resend_invitation.error'),
        })
      } else {
        app.setFlash({
          type: 'correct',
          message: i18n.t('settings.external_users.actions.resend_invitation.success'),
        })
      }
    },
  })

  interface Action {
    key: string
    title: string
    onClick: () => unknown
    danger?: boolean
  }

  const actions: Action[] = []
  if (canInvite && !user.isConfirmed) {
    actions.push({
      key: 'resend_invitation',
      title: i18n.t('settings.external_users.actions.resend_invitation.title'),
      onClick: async () => {
        app.setProgress({
          message: i18n.t('common.saving'),
        })
        await invite({
          id: user.id,
        })
        app.setProgress(null)
      },
    })
  }
  if (canUpdate) {
    actions.push({
      key: 'edit',
      title: i18n.t('settings.permissions.users.edit'),
      onClick: () => setEditModalOpen(true),
    })
  }
  if (canBecome) {
    actions.push({
      key: 'become',
      title: i18n.t('settings.external_users.actions.become.title', {
        first_name: user.firstName,
      }),
      onClick: () => setBecomeModalOpen(true),
    })
  }
  if (!user.isDeactivated) {
    actions.push({
      key: 'deactivate',
      title: i18n.t('settings.external_users.actions.deactivate_user.title'),
      onClick: () => setDeactivateModalOpen(true),
      danger: true,
    })
  }

  return (
    <Box
      width="full"
      flexDirection="row"
      padding="s32"
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: 'grey200',
        },
      }}
      alignItems="center"
    >
      <Box width="full" flexDirection="row" gap="s16" alignItems="center">
        <UserAvatar user={user} />
        <Box width="full">
          <Text size="400" weight="normal" color="grey1000">
            {user.fullName}
          </Text>
          <Text size="300" weight="normal" color="grey600">
            {user.affiliation === 'partner'
              ? i18n.t('common.roles.partner')
              : i18n.t('common.roles.external')}
          </Text>
        </Box>
        {user.affiliation !== 'partner' && <UserStatus user={user} />}
      </Box>
      {actions.length > 0 && (
        <Box paddingLeft="s24">
          <PopoverMenu type="list">
            <PopoverMenu.Trigger>
              <Button.Decorative type="secondary" iconName="ellipsis" />
            </PopoverMenu.Trigger>
            <PopoverMenu.Content side="bottom" align="center">
              {actions.map((action) => (
                <PopoverMenu.TouchableOption
                  key={action.key}
                  title={action.title}
                  onClick={action.onClick}
                  danger={action.danger}
                />
              ))}
            </PopoverMenu.Content>
          </PopoverMenu>
        </Box>
      )}

      {editModalOpen && <EditUserModal userId={user.id} onClose={() => setEditModalOpen(false)} />}
      {becomeModalOpen && (
        <BecomeModal
          accessId={user.id}
          firstName={user.firstName}
          onClose={() => setBecomeModalOpen(false)}
        />
      )}
      {deactivateModalOpen && (
        <DeactivateExternalAccessModal user={user} onClose={() => setDeactivateModalOpen(false)} />
      )}
    </Box>
  )
}

export default Item
